import type { Lens, LensDataset } from '~/types/leLens';

import { outerSource } from './buildPlan';
import { type PlanCatalogueEntry, WORKSPACE_BADGE_LABEL, type WorkspaceCandidate } from './createJourneyTypes';
import { humaniseFilterLabel } from './filterLabels';

const CLAUSE_AFTER_FROM = /\b(where|group\s+by|order\s+by|limit|having|qualify|window|join)\b/i;

/** The rest of the outer query's own FROM clause, trimmed at the next clause
 *  boundary rather than left as everything after FROM (which is what
 *  buildPlan's outerSource intentionally returns, for its own purpose). */
function fromTarget(sql: string): string {
  const rest = outerSource(sql);
  const boundary = CLAUSE_AFTER_FROM.exec(rest);
  return (boundary ? rest.slice(0, boundary.index) : rest).trim();
}

/** A clean label for where a dataset's rows come from. */
export function sourceTableName(sql: string): string {
  const target = fromTarget(sql);
  if (!target) {
    return 'Source not declared';
  }
  if (target.startsWith('(')) {
    return 'A subquery, not a single table';
  }
  return target.replace(/`/g, '');
}

function joinWithAnd(items: Array<string>): string {
  if (items.length <= 1) {
    return items[0] ?? '';
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * One plain sentence naming what makes a row on this dataset. Reads the
 * author's declared grain keys first, since those are what the backend
 * actually checks for uniqueness against the rows a run returns; a dataset
 * saved before grain declaration existed falls back to its declared
 * dimensions, and a dataset with neither says so plainly rather than
 * inventing a grain by parsing its SQL's GROUP BY.
 */
export function describeDatasetGrain(dataset: LensDataset): string {
  const columns = dataset.grain?.keys.length ? dataset.grain.keys : (dataset.dimensions ?? []);
  if (!columns.length) {
    return 'This dataset does not declare which columns set its grain.';
  }
  const labels = columns.map((column) => humaniseFilterLabel(column, dataset.dimensionLabels?.[column]));
  return `One row per ${joinWithAnd(labels)}.`;
}

function catalogueKey(lensId: string, datasetId: string): string {
  return `${lensId}:${datasetId}`;
}

/** Builds one candidate from a dataset already declared on a Lens the viewer
 *  can read. Pure and synchronous: every field comes from data the list
 *  endpoint already returned, so listing candidates costs nothing further.
 *
 *  Columns come from the dataset's own declared list, from a BigQuery dry
 *  run, never guessed from its SQL text: a dataset saved before that existed
 *  hands back no columns rather than a regex guess, which is the "not yet
 *  declared" state a caller renders as a prompt to declare them, not a
 *  reason to fabricate a list that can be silently wrong. */
export function workspaceCandidateFrom(lens: Pick<Lens, 'id' | 'title'>, dataset: LensDataset): WorkspaceCandidate {
  return {
    id: catalogueKey(lens.id, dataset.id),
    name: dataset.name,
    source: sourceTableName(dataset.sql),
    grain: describeDatasetGrain(dataset),
    columns: (dataset.columns ?? []).map((column) => column.name),
    lensId: lens.id,
    lensTitle: lens.title,
    datasetId: dataset.id,
    badge: WORKSPACE_BADGE_LABEL,
    dataset,
  };
}

/** Every candidate across every dashboard the viewer can read, in the order
 *  the list endpoint returned them. */
export function workspaceCandidatesFromLenses(lenses: Array<Lens>): Array<WorkspaceCandidate> {
  return lenses.flatMap((lens) => (lens.spec.datasets ?? []).map((dataset) => workspaceCandidateFrom(lens, dataset)));
}

/** The subset of a candidate the planning endpoint's catalogue accepts. */
export function toCatalogueEntry(candidate: WorkspaceCandidate): PlanCatalogueEntry {
  const { id, name, source, grain, columns } = candidate;
  return { id, name, source, grain, columns };
}
